import { Container, Text, TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { HUD, NOTICE_STYLE, type NoticeKind } from '@/config/hud';
import { Pool } from '@/core/pool';
import { UI_FONT } from './widgets';

const CFG = HUD.notices;

interface NoticeLine {
  text: Text;
  msg: string;
  priority: number;
  seq: number;
  /** Seconds since shown / total lifetime (fadeIn + hold + fadeOut) */
  age: number;
  life: number;
  targetY: number;
}

interface Pending {
  msg: string;
  kind: NoticeKind;
  color: number;
  seq: number;
  /** Seconds spent in the queue — stale entries are dropped */
  waited: number;
}

/**
 * Top-centre notice band under the boss bar (docs/06, E6). Messages QUEUE by
 * priority instead of overwriting each other: up to `maxVisible` lines stack,
 * a waiting higher-priority message cuts the weakest line short, duplicates
 * just refresh. Text objects and queue entries are pooled — update() never
 * allocates.
 */
export class NoticeQueue {
  readonly view = new Container();
  private readonly lines = new Pool<NoticeLine>(() => this.makeLine(), CFG.maxVisible);
  private readonly entries = new Pool<Pending>(
    () => ({ msg: '', kind: 'wave', color: 0, seq: 0, waited: 0 }), CFG.queueCap,
  );
  private readonly active: NoticeLine[] = [];
  /** Sorted: highest priority first, FIFO inside a priority */
  private readonly pending: Pending[] = [];
  private seq = 0;
  private scale = 1;
  private maxW = 1000;

  private makeLine(): NoticeLine {
    const text = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: UI_FONT, fontSize: 28, fill: COLORS.playerYellow, letterSpacing: 3,
        stroke: { color: COLORS.outline, width: 5 },
      }),
    });
    text.anchor.set(0.5, 0);
    text.visible = false;
    this.view.addChild(text);
    return { text, msg: '', priority: 0, seq: 0, age: 0, life: 0, targetY: 0 };
  }

  /** Band centre/top (screen px), max line width, HUD scale for small viewports. */
  layout(centreX: number, bandTop: number, maxW: number, scale: number): void {
    this.view.position.set(centreX, bandTop);
    this.scale = scale;
    this.maxW = maxW;
    // Resize / first layout: refit lines already on screen
    for (const line of this.active) this.fit(line.text);
    this.restack('all');
  }

  /** HUD scale, shrunk further if the line is wider than the band. */
  private fit(text: Text): void {
    text.scale.set(1);
    text.scale.set(Math.min(this.scale, this.maxW / Math.max(1, text.width)));
  }

  push(msg: string, kind: NoticeKind, color: number = NOTICE_STYLE[kind].color): void {
    const style = NOTICE_STYLE[kind];
    // Duplicate on screen → refresh its hold instead of stacking a copy
    for (const line of this.active) {
      if (line.msg === msg) {
        line.age = Math.min(line.age, CFG.fadeIn);
        return;
      }
    }
    for (const p of this.pending) if (p.msg === msg) return;

    if (this.active.length < CFG.maxVisible) {
      this.show(msg, kind, color, this.seq++);
      return;
    }
    this.enqueue(msg, kind, color);
    // Everything visible is weaker → fade the weakest/oldest line out early
    let weakest: NoticeLine | null = null;
    for (const line of this.active) {
      if (line.priority >= style.priority) continue;
      if (!weakest || line.priority < weakest.priority ||
        (line.priority === weakest.priority && line.seq < weakest.seq)) weakest = line;
    }
    if (weakest) {
      weakest.age = Math.max(weakest.age, weakest.life - CFG.preemptRemaining);
    }
  }

  private enqueue(msg: string, kind: NoticeKind, color: number): void {
    const priority = NOTICE_STYLE[kind].priority;
    if (this.pending.length >= CFG.queueCap) {
      const last = this.pending[this.pending.length - 1]!;
      if (NOTICE_STYLE[last.kind].priority >= priority) return; // queue full of stronger news
      this.entries.release(this.pending.pop()!);
    }
    const e = this.entries.acquire();
    e.msg = msg;
    e.kind = kind;
    e.color = color;
    e.seq = this.seq++;
    e.waited = 0;
    // Insert after every entry of >= priority (stable FIFO)
    let i = this.pending.length;
    this.pending.push(e);
    while (i > 0 && NOTICE_STYLE[this.pending[i - 1]!.kind].priority < priority) {
      this.pending[i] = this.pending[i - 1]!;
      i--;
    }
    this.pending[i] = e;
  }

  private show(msg: string, kind: NoticeKind, color: number, seq: number): void {
    const style = NOTICE_STYLE[kind];
    const line = this.lines.acquire();
    line.msg = msg;
    line.priority = style.priority;
    line.seq = seq;
    line.age = 0;
    line.life = CFG.fadeIn + style.hold + CFG.fadeOut;
    const text = line.text;
    text.style.fontSize = style.fontSize;
    text.style.fill = color;
    text.text = msg;
    this.fit(text);
    text.alpha = 0;
    text.visible = true;
    this.active.push(line);
    this.restack('new');
  }

  /** Strongest line on top; `snap` lines jump to their slot, the rest slide. */
  private restack(snap: 'all' | 'new' | 'none'): void {
    const a = this.active;
    // Insertion sort in place: priority desc, then arrival order
    for (let i = 1; i < a.length; i++) {
      const cur = a[i]!;
      let j = i - 1;
      while (j >= 0 && (a[j]!.priority < cur.priority ||
        (a[j]!.priority === cur.priority && a[j]!.seq > cur.seq))) {
        a[j + 1] = a[j]!;
        j--;
      }
      a[j + 1] = cur;
    }
    let y = 0;
    for (const line of a) {
      line.targetY = y;
      if (snap === 'all' || (snap === 'new' && line.age === 0)) line.text.y = y;
      y += line.text.height + CFG.lineGap * this.scale;
    }
  }

  update(dt: number): void {
    let expired = false;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const line = this.active[i]!;
      line.age += dt;
      const t = line.text;
      if (line.age >= line.life) {
        t.visible = false;
        this.active[i] = this.active[this.active.length - 1]!;
        this.active.pop();
        this.lines.release(line);
        expired = true;
        continue;
      }
      const fadeOutAt = line.life - CFG.fadeOut;
      t.alpha = line.age < CFG.fadeIn
        ? line.age / CFG.fadeIn
        : line.age > fadeOutAt ? Math.max(0, (line.life - line.age) / CFG.fadeOut) : 1;
      t.y += (line.targetY - t.y) * Math.min(1, dt * 14);
    }
    // Age the queue; compact in place (no allocation)
    let w = 0;
    for (let r = 0; r < this.pending.length; r++) {
      const e = this.pending[r]!;
      e.waited += dt;
      if (e.waited > CFG.maxWait) {
        this.entries.release(e);
        continue;
      }
      this.pending[w++] = e;
    }
    this.pending.length = w;
    if (expired) {
      while (this.active.length < CFG.maxVisible && this.pending.length > 0) {
        const e = this.pending.shift()!;
        this.show(e.msg, e.kind, e.color, e.seq);
        this.entries.release(e);
      }
      this.restack('none');
    }
  }
}
